import { auth, safeLogEvent, analytics } from '../firebase';

/**
 * Worker job-link funnel — GA4 (best-effort; no-op when analytics is off).
 * Added 2026-10-23 after a 174-worker SMS blast produced zero applicants and
 * there was no way to tell "never opened" from "opened, got stuck".
 *
 * Read in GA4 (Explore → Funnel): job_link_landed → job_view → apply_click /
 * claim_click → (apply_step_viewed… | claim_sheet_open → claim_submit →
 * claim_success). Failures: job_load_error, claim_error, js_error. Every event
 * carries `via_invite` (the SMS blast link, ?invite=1), `signed_in`, `post_id`.
 * Free-form clicks are logged by InteractionTracker as `ui_click`.
 */

const SESSION_KEY = 'hrx_job_funnel_ctx';

export interface FunnelContext {
  via_invite: boolean;
  utm_source?: string;
  utm_campaign?: string;
}

/** Postings opened from the blast carry ?invite=1; remember it for the whole tab session (apply wizard drops the param). */
export function captureFunnelContext(search: string): FunnelContext {
  const qs = new URLSearchParams(search);
  const fresh: FunnelContext = {
    via_invite: qs.get('invite') === '1',
    ...(qs.get('utm_source') ? { utm_source: qs.get('utm_source') as string } : {}),
    ...(qs.get('utm_campaign') ? { utm_campaign: qs.get('utm_campaign') as string } : {}),
  };
  try {
    if (fresh.via_invite || fresh.utm_source) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(fresh));
      return fresh;
    }
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) return JSON.parse(saved) as FunnelContext;
  } catch {
    /* private mode — fall through */
  }
  return fresh;
}

/** Post id from /…/jobs-board/:postId or /apply/:tenant/:postId. */
export function postIdFromPath(pathname: string): string | undefined {
  const m = pathname.match(/\/jobs-board\/([^/?#]+)/) || pathname.match(/^\/apply\/[^/]+\/([^/?#]+)/);
  return m ? m[1] : undefined;
}

export function trackFunnelEvent(name: string, params: Record<string, unknown> = {}): void {
  try {
    const ctx = captureFunnelContext(typeof window !== 'undefined' ? window.location.search : '');
    safeLogEvent(analytics, name, {
      via_invite: ctx.via_invite,
      ...(ctx.utm_source ? { utm_source: ctx.utm_source } : {}),
      signed_in: Boolean(auth.currentUser?.uid),
      page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      post_id: typeof window !== 'undefined' ? postIdFromPath(window.location.pathname) : undefined,
      ...params,
    });
  } catch {
    /* analytics must never break the page */
  }
}
